import axios from "axios";
import moment from "moment";
import i18n from "../../../i18n";
import {
  HttpCodes,
  HTTP_REQUEST_CUSTOM_ERROR,
  getBaseDateFormat,
} from "../../../modules/constants";
import { handleHttpErrors } from "../../../modules/http";
import { getClaims } from "../../../modules/login";

import { NUTRITION_FIELDS } from "./DietDiary.constants";

const canAddNoteMealLog = (state) => getClaims(state, "mealLog.canAddNote");
const canViewDetailMealLog = (state) =>
  getClaims(state, "mealLog.detail.canView");
const canCreateFavorites = (state) => getClaims(state, "favorites.canCreate");

//Constants
const SET_NUTRITION_VALUES = "SET_NUTRITION_VALUES";
const SET_MEAL_TOTAL_CALORIES = "SET_MEAL_TOTAL_CALORIES";
const SET_MEAL_CHARTS_HELPER = "SET_MEAL_CHARTS_HELPER";
const SET_ADD_DIET_DIARY_SUCCESS = "SET_ADD_DIET_DIARY_SUCCESS";
const SET_ADD_DIET_DIARY_ERROR = "SET_ADD_DIET_DIARY_ERROR";
const SET_NUTRITION_FIELDS = "SET_NUTRITION_FIELDS";
const SET_FOOD_DRINK_ITEMS = "SET_FOOD_DRINK_ITEMS";
const SET_DIET_DIARY_MEAL_DATE_TIME = "SET_DIET_DIARY_MEAL_DATE_TIME";
const SET_DIET_DIARY_ABOUT_MEAL = "SET_DIET_DIARY_ABOUT_MEAL";
const SET_DIET_DIARY_DATE_EXISTING_MEALS = "SET_DIET_DIARY_DATE_EXISTING_MEALS";
const SET_UPDATE_REASON_FOR = "SET_UPDATE_REASON_FOR";
const DIET_DIARY_RESET_STATES = "DIET_DIARY_RESET_STATES";
const SET_PARTICIPANT_TIME_FORMAT = "SET_PARTICIPANT_TIME_FORMAT";
const LOGOUT = "LOGOUT";

//Acessors
const getNutritionalInfo = (state) => state?.nutritionalInfo;
const getCalories = (state) => state?.calories;
const getChartsHelper = (state) => state?.chartsHelper;
const getIsSuccess = (state) => state?.isSuccess;
const getIsError = (state) => state?.isError;
const getNutritionFields = (state) => state?.nutritionFields;
const getSelectedItems = (state) => state?.selectedItems;
const getDateAndTimeValues = (state) => state?.dateAndTimeValues;
const getAboutMeal = (state) => state?.aboutMeal;
const getExistingDayMeals = (state) => state?.existingDayMeals;
const getUpdateReasonFor = (state) => state?.updateReasonFor;
const getDailyTargets = (state) => state?.dietDiaryChartsReducer?.dailyTargets;
const getParticipantTimeFormat = (state) => state?.participantTimeFormat;

//Default state
const initialState = {
  nutritionalInfo: [],
  calories: {
    value: "0.0kcal",
  },
  chartsHelper: {
    pieChartSeries: [],
    hpbcImpact: [],
  },
  isSuccess: false,
  isError: false,
  nutritionFields: [],
  selectedItems: [],
  dateAndTimeValues: {
    isValid: false,
    dateVals: null,
  },
  aboutMeal: null,
  existingDayMeals: [],
  updateReasonFor: "",
  participantTimeFormat: null,
};

export const dietDiaryReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_NUTRITION_VALUES: {
      const { nutritionalInfo } = action;
      return Object.assign({}, state, {
        nutritionalInfo: nutritionalInfo,
      });
    }
    case SET_MEAL_TOTAL_CALORIES: {
      const { calories } = action;
      return Object.assign({}, state, {
        calories: calories,
      });
    }
    case SET_MEAL_CHARTS_HELPER: {
      const { chartsHelper } = action;
      return Object.assign({}, state, {
        chartsHelper: chartsHelper,
      });
    }
    case SET_ADD_DIET_DIARY_SUCCESS: {
      const { isSuccess } = action;
      return Object.assign({}, state, {
        isSuccess: isSuccess,
      });
    }
    case SET_ADD_DIET_DIARY_ERROR: {
      const { isError } = action;
      return Object.assign({}, state, {
        isError: isError,
      });
    }
    case SET_NUTRITION_FIELDS: {
      const { nutritionFields } = action;
      return Object.assign({}, state, {
        nutritionFields: nutritionFields,
      });
    }
    case SET_FOOD_DRINK_ITEMS: {
      const { selectedItems } = action;
      return Object.assign({}, state, {
        selectedItems: selectedItems,
      });
    }
    case SET_DIET_DIARY_MEAL_DATE_TIME: {
      const { dateAndTimeValues } = action;
      return Object.assign({}, state, {
        dateAndTimeValues: dateAndTimeValues,
      });
    }
    case SET_DIET_DIARY_ABOUT_MEAL: {
      const { aboutMeal } = action;
      return Object.assign({}, state, {
        aboutMeal: aboutMeal,
      });
    }
    case SET_DIET_DIARY_DATE_EXISTING_MEALS: {
      const { allMeals } = action;
      return Object.assign({}, state, {
        existingDayMeals: allMeals,
      });
    }
    case SET_UPDATE_REASON_FOR: {
      const { updateReasonFor } = action;
      return Object.assign({}, state, {
        updateReasonFor: updateReasonFor,
      });
    }
    case DIET_DIARY_RESET_STATES: {
      return Object.assign({}, initialState, {
        nutritionFields: state.nutritionFields,
      });
    }
    case SET_PARTICIPANT_TIME_FORMAT: {
      const { participantTimeFormat } = action;

      return Object.assign({}, state, {
        participantTimeFormat: participantTimeFormat,
      });
    }
    case LOGOUT:
      return initialState;
    default:
      return state;
  }
};

//Mutators
export const resetStates = () => (dispatch) => {
  return dispatch({
    type: DIET_DIARY_RESET_STATES,
  });
};

export const setNutritionalInfo = (newValues) => (dispatch) => {
  return dispatch({
    type: SET_NUTRITION_VALUES,
    nutritionalInfo: newValues,
  });
};

export const setSelectedItems = (items) => (dispatch) => {
  return dispatch({
    type: SET_FOOD_DRINK_ITEMS,
    selectedItems: items,
  });
};

export const setCalories = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_MEAL_TOTAL_CALORIES,
    calories: newValue,
  });
};

export const setChartsHelper = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_MEAL_CHARTS_HELPER,
    chartsHelper: newValue,
  });
};

export const setIsSuccess = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_ADD_DIET_DIARY_SUCCESS,
    isSuccess: newValue,
  });
};

export const setIsError = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_ADD_DIET_DIARY_ERROR,
    isError: newValue,
  });
};

export const setDateAndTimeValues = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_DIET_DIARY_MEAL_DATE_TIME,
    dateAndTimeValues: newValue,
  });
};

export const setAboutMeal = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_DIET_DIARY_ABOUT_MEAL,
    aboutMeal: newValue,
  });
};

export const setUpdateReasonFor = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_UPDATE_REASON_FOR,
    updateReasonFor: newValue,
  });
};

const fetchNutritionFields = () => (dispatch) => {
  dispatch({ type: SET_NUTRITION_FIELDS, nutritionFields: NUTRITION_FIELDS });
};

const saveDietDiaryMeal = (formData, editing) => async (dispatch, getState) => {
  if (editing) {
    return axios
      .put(`/api/diet/meal`, formData)
      .then(() => {
        return Promise.resolve(
          i18n.t("appText.dietDiary.entry.update.success")
        );
      })
      .catch((error) => {
        if (error === HTTP_REQUEST_CUSTOM_ERROR.accessDenied) {
          return;
        }

        let badRequestMessage = null;
        let conflictMessage = null;

        if (error?.response?.status === HttpCodes.CONFLICT) {
          conflictMessage = i18n.t("errorText.dietDiary.meal.add.400", {
            date: moment(formData.mealDate).format(getBaseDateFormat()),
          });
        }

        return handleHttpErrors(error.request?.status, {
          default: i18n.t("errorText.dietDiary.entry.edit"),
          badRequest: badRequestMessage,
          conflict: conflictMessage,
        });
      });
  } else {
    return axios
      .post(`/api/diet/meal`, formData)
      .then(() => {
        return Promise.resolve(i18n.t("appText.dietDiary.entry.add.success"));
      })
      .catch((error) => {
        if (error === HTTP_REQUEST_CUSTOM_ERROR.accessDenied) {
          return;
        }

        let badRequestMessage = null;
        let conflictMessage = null;

        if (error?.response?.status === HttpCodes.BAD_REQUEST) {
          if (error?.response?.data === "Invalid Date.") {
            badRequestMessage = i18n.t("errorText.dietDiary.entry.add");
          }
        }

        if (error?.response?.status === HttpCodes.CONFLICT) {
          conflictMessage = i18n.t("errorText.dietDiary.meal.add.400", {
            date: moment(formData.mealDate).format(getBaseDateFormat()),
          });
        }

        return handleHttpErrors(error.request?.status, {
          default: i18n.t("errorText.dietDiary.entry.add"),
          badRequest: badRequestMessage,
          conflict: conflictMessage,
        });
      });
  }
};

const fetchParticipantTimeFormat = (participantId) => async (dispatch) => {
  const params = {
    participantId,
  };

  return axios
    .get(`/api/participant/info`, { params })
    .then(({ data }) => {
      dispatch({
        type: SET_PARTICIPANT_TIME_FORMAT,
        participantTimeFormat: data.timeFormat,
      });
    })
    .catch(() => {
      dispatch({
        type: SET_PARTICIPANT_TIME_FORMAT,
        participantTimeFormat: null,
      });
    });
};

export const mapStateToProps = (state) => {
  return {
    nutritionalInfo: getNutritionalInfo(state),
    calories: getCalories(state),
    chartsHelper: getChartsHelper(state),
    isSuccess: getIsSuccess(state),
    isError: getIsError(state),
    nutritionFields: getNutritionFields(state),
    selectedItems: getSelectedItems(state),
    dateAndTimeValues: getDateAndTimeValues(state),
    aboutMeal: getAboutMeal(state),
    existingDayMeals: getExistingDayMeals(state),
    updateReasonFor: getUpdateReasonFor(state),
    canAddNoteMealLog: canAddNoteMealLog(state),
    canViewDetailMealLog: canViewDetailMealLog(state),
    canCreateFavorites: canCreateFavorites(state),
    dailyTargets: getDailyTargets(state),
    participantTimeFormat: getParticipantTimeFormat(state),
  };
};

export const mapDispatchToProps = {
  setNutritionalInfo,
  setSelectedItems,
  setCalories,
  setChartsHelper,
  setIsSuccess,
  setIsError,
  setDateAndTimeValues,
  setAboutMeal,
  setUpdateReasonFor,
  saveDietDiaryMeal,
  fetchNutritionFields,
  resetStates,
  fetchParticipantTimeFormat,
};
